import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import * as Sentry from "@sentry/remix";
import { registerLocale } from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import type { InitOptions } from "i18next";
import i18next from "i18next";
import detector from "i18next-browser-languagedetector";
import HttpApi from "i18next-http-backend";
import { useEffect } from "react";
import { hydrate } from "react-dom";
import { I18nextProvider } from "react-i18next";
import { fallbackLng, I18N_COOKIE_NAME, supportedLanguages } from "./i18n";
import { SENTRY_DSN } from "./sentry";
import { registerDatePickerLocales } from "./utils/format";
import { APP_VERSION } from "./version";

console.log(`APP_VERSION: ${APP_VERSION}`);

if (window.GLOBALS?.APP_ENV === "production") {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: window.GLOBALS?.APP_ENV,
    release: window.GLOBALS?.APP_VERSION,
    tracesSampleRate: 0,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.remixRouterInstrumentation(
          useEffect,
          useLocation,
          useMatches
        ),
      }),
    ],
    denyUrls: [
      // Facebook flakiness
      /graph\.facebook\.com/i,
      // Facebook blocked
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
    ],
  });
}

let i18n = i18next.use(detector);

const opts: InitOptions = {
  supportedLngs: supportedLanguages,
  fallbackLng,
  react: { useSuspense: false },
  detection: {
    order: ["cookie", "navigator"],
    lookupFromPathIndex: 0,
    lookupCookie: I18N_COOKIE_NAME,
    caches: ["cookie"],
  },
};

if (process.env.NODE_ENV === "development") {
  i18n = i18n.use(
    new HttpApi(null, {
      loadPath: "http://localhost:6007/locales/{{lng}}.json",
      addPath: "http://localhost:6007/locales",
    })
  );

  opts.debug = true;
  opts.saveMissing = true;
  opts.saveMissingTo = "fallback";
} else {
  i18n = i18n.use(
    new HttpApi(null, {
      loadPath: `/locales/{{lng}}.json?v=${APP_VERSION}`,
      addPath: "/locales",
      crossDomain: true,
    })
  );
}

i18n.init(opts, () => {
  registerLocale(en);
  registerDatePickerLocales();

  hydrate(
    <I18nextProvider i18n={i18n}>
      <RemixBrowser />
    </I18nextProvider>,
    document as any
  );
});
